import type {
  EnvironmentId,
  PullRequestActivity,
  PullRequestDetail,
  PullRequestRef,
} from "@cmmd-forge/contracts";

import {
  baseComparisonLabel,
  mergeabilityLabel,
  reviewerEntries,
  summaryActivityEnrichment,
} from "./pullRequestSummary.logic";
import { PullRequestDescriptionEditor } from "./PullRequestDescriptionEditor";
import { PullRequestReviewers } from "./PullRequestReviewers";
import { PullRequestSummaryConversation } from "./PullRequestSummaryConversation";
import { PullRequestDescriptionReactions } from "./PullRequestDescriptionReactions";
import { PullRequestSummarySection } from "./PullRequestSummarySection";
import {
  PullRequestSummaryChecks,
  PullRequestSummaryComments,
  PullRequestSummaryHeader,
  PullRequestSummaryLabels,
  PullRequestSummaryStatus,
} from "./PullRequestSummary.renderers";

export interface PullRequestSummaryProps {
  readonly detail: PullRequestDetail;
  readonly activity: PullRequestActivity | undefined;
  readonly activityPending?: boolean;
  readonly activityError?: Error | null;
  readonly onRetryActivity?: () => void;
  readonly editable: boolean;
  readonly pending: boolean;
  readonly onSaveDescription: (body: string) => Promise<void>;
  readonly environmentId: EnvironmentId;
  readonly reference: PullRequestRef;
  readonly isDetailFresh: boolean;
}

export function PullRequestSummary({
  detail,
  activity,
  activityPending = false,
  activityError = null,
  onRetryActivity,
  editable,
  pending,
  onSaveDescription,
  environmentId,
  reference,
  isDetailFresh,
}: PullRequestSummaryProps) {
  const enrichment = summaryActivityEnrichment(detail, activity);
  return (
    <section aria-label="Pull request summary">
      <div className="space-y-3 px-4 py-3">
        <PullRequestSummaryHeader detail={detail} />
        <PullRequestSummaryStatus
          author={enrichment.author}
          autoMergeEnabled={detail.autoMergeEnabled}
          baseComparison={baseComparisonLabel(detail.baseComparison, detail.behindBy)}
          mergeabilityLabel={mergeabilityLabel(detail.mergeability)}
        />
        <PullRequestReviewers
          environmentId={environmentId}
          reference={reference}
          detail={detail}
          reviewers={enrichment.reviewers}
          reviewerEntries={reviewerEntries(
            enrichment.reviewers,
            activity?.comments ?? [],
            activity?.commits ?? [],
          )}
          isDetailFresh={isDetailFresh}
        />
        {detail.labels.length > 0 ? <PullRequestSummaryLabels labels={detail.labels} /> : null}
        <PullRequestSummaryComments count={activity?.commentCount ?? 0} />
      </div>
      <PullRequestSummarySection title="Description">
        <PullRequestDescriptionEditor
          body={detail.body}
          editable={editable}
          pending={pending}
          onSave={onSaveDescription}
        />
        <PullRequestDescriptionReactions
          activity={activity}
          detail={detail}
          environmentId={environmentId}
          reference={reference}
          isDetailFresh={isDetailFresh}
        />
      </PullRequestSummarySection>
      <PullRequestSummarySection title="Checks" count={detail.checks.length}>
        <PullRequestSummaryChecks checks={detail.checks} />
      </PullRequestSummarySection>
      <PullRequestSummaryConversation
        detail={detail}
        activity={activity}
        pending={activityPending}
        error={activityError}
        onRetry={onRetryActivity}
        environmentId={environmentId}
        reference={reference}
        isDetailFresh={isDetailFresh}
      />
    </section>
  );
}
